import {
	Alert,
	Anchor,
	Button,
	Card,
	Grid,
	Group,
	Skeleton,
	Stack,
	Switch,
	Text,
	Title,
} from "@mantine/core";
import { IconAlertCircle, IconLock } from "@tabler/icons-react";
import { Link, useParams } from "@tanstack/react-router";
import { useSnapshot } from "valtio";
import { assistantLogDetailTexts } from "@/locales/assistant-log-detail";
import { i18nStore } from "@/store/i18n";
import {
	formatLogCount,
	formatLogDate,
	formatLogTime,
} from "./log-format.logic";
import { LogTranscriptBubble } from "./log-transcript-bubble";
import { LogTranscriptInfo } from "./log-transcript-info";
import { useAssistantLogTranscript } from "./use-assistant-log-transcript";

const INFO_COL = { base: 12, lg: 4 };
const CHAT_COL = { base: 12, lg: 8 };

/**
 * Lapis 3 (`/admin/ai-assistant/log/$userId/$conversationId`): transkrip
 * percakapan. Membuka halaman ini butuh izin transkrip terpisah dan DICATAT
 * di Audit Log oleh server — banner kuning memberi tahu admin soal itu
 * (rancangan 08 §3 & §6).
 *
 * Ekspor transkrip belum ada di versi ini — tercatat sebagai task terbuka.
 */
export function LogTranscriptPage() {
	const { userId, conversationId } = useParams({
		from: "/admin/ai-assistant/log/$userId/$conversationId",
	});
	const { lang: uiLang } = useSnapshot(i18nStore);
	const lang = uiLang === "en" ? "en" : "id";
	const td = assistantLogDetailTexts[lang];
	const state = useAssistantLogTranscript(conversationId);

	return (
		<Stack gap="lg">
			<Stack gap={4}>
				{/* `renderRoot`, bukan `component={Link}`: prop polimorfik Mantine tidak
				    meneruskan tipe generik TanStack, sehingga `params` kehilangan tipenya. */}
				<Anchor
					renderRoot={(props) => (
						<Link
							to="/admin/ai-assistant/log/$userId"
							params={{ userId }}
							{...props}
						/>
					)}
					size="sm"
				>
					{td.backToUser}
				</Anchor>
				<Title order={3}>{state.meta?.title ?? td.breadcrumbRoot}</Title>
				{state.meta && (
					<Text size="sm" c="dimmed">
						{td.transcriptMeta(
							formatLogDate(state.meta.startedAt, lang),
							formatLogTime(state.meta.startedAt, lang),
							formatLogTime(state.meta.lastMessageAt, lang),
							formatLogCount(state.meta.messages, lang),
							formatLogCount(state.meta.models.length, lang),
							formatLogCount(state.meta.errors, lang),
						)}
					</Text>
				)}
			</Stack>

			{state.forbidden && (
				<Alert color="red" icon={<IconLock size={16} />} data-log-forbidden>
					{td.transcriptForbidden}
				</Alert>
			)}
			{state.notFound && (
				<Alert color="yellow" icon={<IconAlertCircle size={16} />}>
					{td.transcriptNotFound}
				</Alert>
			)}
			{state.error && (
				<Alert color="red" icon={<IconAlertCircle size={16} />}>
					{state.error}
				</Alert>
			)}

			{!state.forbidden && !state.notFound && (
				<>
					<Alert color="yellow" icon={<IconAlertCircle size={16} />}>
						{td.auditBanner}
					</Alert>

					<Group justify="flex-end">
						<Switch
							size="sm"
							label={state.hideContent ? td.showContent : td.hideContent}
							checked={state.hideContent}
							onChange={(event) =>
								state.setHideContent(event.currentTarget.checked)
							}
						/>
					</Group>

					{state.loading ? (
						<Skeleton height={320} radius="md" data-log-transcript-loading />
					) : (
						<Grid gutter="lg">
							<Grid.Col span={CHAT_COL}>
								<Stack gap="md">
									{state.prevCursor ? (
										<Button
											variant="subtle"
											size="xs"
											loading={state.loadingMore}
											onClick={state.loadPrevious}
										>
											{td.loadPrevious(
												String(state.messages.length),
												formatLogCount(state.meta?.messages ?? 0, lang),
											)}
										</Button>
									) : (
										<Text size="xs" c="dimmed" ta="center">
											{td.transcriptStart}
										</Text>
									)}

									{state.messages.map((message) => (
										<LogTranscriptBubble key={message.id} message={message} />
									))}

									{state.contentHidden && (
										<Card withBorder radius="md" p="sm">
											<Text size="xs" c="dimmed">
												{td.contentHiddenNote}
											</Text>
										</Card>
									)}
								</Stack>
							</Grid.Col>
							<Grid.Col span={INFO_COL}>
								{state.meta && (
									<LogTranscriptInfo
										meta={state.meta}
										user={state.user}
										retentionDays={state.retentionDays}
									/>
								)}
							</Grid.Col>
						</Grid>
					)}
				</>
			)}
		</Stack>
	);
}
